import * as React from 'react';
import { withRouter, RouteComponentProps } from 'react-router-dom';

import withStyles, {
  WithStyles,
  StyleRulesCallback
} from '@material-ui/core/styles/withStyles';

import 'leaflet/dist/leaflet.css';
import 'leaflet-draw/dist/leaflet.draw.css';

import {
  DocumentGrid
} from '../components';

import {
  LayoutToolsProvider,
  LayoutTools,
  FarmProvider,
  FarmLibraryProvider,
  CreateReportProvider,
  FarmFieldsProvider
} from '../providers';

import { DatePicker } from 'material-ui-pickers';

import {
  Paper,
  Typography,
  Button,
  CircularProgress,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ListItemText,
  Chip,
  Checkbox
} from '@material-ui/core';

import BackIcon from '@material-ui/icons/ArrowBack';

import { Stepper, Step, StepLabel, StepContent } from '@material-ui/core';
import SidePanel from './SidePanel';

interface Renders {
  layout: LayoutTools;
  loading: boolean;
  farm: Farm | null;
  createReport: () => void;
}

interface State {
  activeStep: number;
  report: ReportDocument | null;
  fieldId?: string;
  fieldIds?: string[];
  documentId?: string;
  documentIds?: string[];
  sinceDate?: number;
  untilDate?: number;
  zoneAnalysisDocumentId?: string;
}

const styles: StyleRulesCallback = theme => ({
  root: {
    flex: 1,
    display: 'flex',
    flexDirection: 'row',
    height: '100vh'
  },
  stepper: {
    marginTop: theme.spacing.unit * 2
  },
  content: {
    flex: 1,
    padding: theme.spacing.unit * 3,
    overflowY: 'scroll',
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'flex-start',
    justifyContent: 'center'
  },
  form: {
    flexBasis: 600,
    flex: 0
  },
  contentCentered: {
    flex: 1,
    padding: theme.spacing.unit * 3,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center'
  },
  map: {
    flex: 1
  }
});

type Props = WithStyles & RouteComponentProps<any>;

class ReportsCreation extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      activeStep: 0,
      report: null
    };
  }

  getReportType = (): string => {
    const {
      match: {
        params: { type }
      }
    } = this.props;
    switch (type) {
      case 'tracking-field':
        return 'Seguimiento de lote';
      case 'tracking-multiple-fields':
        return 'Seguimiento de múltiples lotes';
      case 'raster':
        return 'Raster individual';
      case 'raster-zone':
        return 'Raster por zona de gestión';
      case 'raster-compare':
        return 'Comparación de rasters';
      default:
        return '';
    }
  }

  onReportCreated = (report: ReportDocument) => {
    const {
      match: {
        params: { type }
      }
    } = this.props;
    if (type !== 'tracking-field') {
      this.setState({ activeStep: 2, report });
    } else {
      this.setState({ activeStep: 3, report });
    }
  }
  
  renderFieldStepper(renders: Renders) {
    const { classes } = this.props;
    const { fieldId, zoneAnalysisDocumentId, report } = this.state;
    return (
      <Stepper
        activeStep={this.state.activeStep}
        orientation="vertical"
        className={classes.stepper}
      >
        <Step key={0}>
          <StepLabel>Seleccionar Lote</StepLabel>
          <StepContent>
            <Typography variant="subheading" gutterBottom={true}>
              Elija de su biblioteca el lote que desea utilizar en el reporte
            </Typography>
            <Button
              fullWidth={true}
              color="primary"
              variant="raised"
              disabled={!fieldId}
              onClick={() => this.setState({ activeStep: 1 })}
            >
              Seleccionar
            </Button>
            {!fieldId && (
              <Typography color="error">
                Debe seleccionar un lote
              </Typography>
            )}
          </StepContent>
        </Step>
        <Step key={1}>
          <StepLabel>Seleccionar Zona de Gestión</StepLabel>
          <StepContent>
            <Typography variant="subheading" gutterBottom={true}>
              Elija de su biblioteca el raster de zona de gestión que desea utilizar en el reporte
            </Typography>
            <Button
              fullWidth={true}
              color="primary"
              variant="raised"
              disabled={!zoneAnalysisDocumentId}
              onClick={() => {
                this.setState({ activeStep: 2 });
                renders.createReport();
              }}
            >
              Seleccionar
            </Button>
            {!zoneAnalysisDocumentId && (
              <Typography color="error">
                Debe seleccionar un raster
              </Typography>
            )}
          </StepContent>
        </Step>
        <Step key={2}>
          <StepLabel>Crear Reporte</StepLabel>
          <StepContent>
            <Typography variant="subheading">
              El servidor esta creando el reporte
            </Typography>
          </StepContent>
        </Step>
        <Step key={3}>
          <StepLabel>Validar Resultado</StepLabel>
          <StepContent>
            <Typography variant="subheading" gutterBottom={true}>
              Este es el resultado del reporte
            </Typography>
            <Button
              fullWidth={true}
              color="primary"
              variant="raised"
              onClick={() => window.open((report as ReportDocument).downloadUrl, '_blank')}
            >
              Descargar
            </Button>
          </StepContent>
        </Step>
      </Stepper>
    );
  }

  renderFieldsStepper(renders: Renders) {
    const { classes } = this.props;
    const { fieldIds, report } = this.state;
    return (
      <Stepper
        activeStep={this.state.activeStep}
        orientation="vertical"
        className={classes.stepper}
      >
        <Step key={0}>
          <StepLabel>Seleccionar Lotes</StepLabel>
          <StepContent>
            <Typography variant="subheading" gutterBottom={true}>
              Elija de su biblioteca los lotes que desea utilizar en el reporte
            </Typography>
            <Button
              fullWidth={true}
              color="primary"
              variant="raised"
              disabled={!fieldIds || !fieldIds.length}
              onClick={() => {
                this.setState({ activeStep: 1 });
                renders.createReport();
              }}
            >
              Seleccionar
            </Button>
            {(!fieldIds || !fieldIds.length) && (
              <Typography color="error">
                Debe seleccionar por lo menos un lote
              </Typography>
            )}
          </StepContent>
        </Step>
        <Step key={1}>
          <StepLabel>Crear Reporte</StepLabel>
          <StepContent>
            <Typography variant="subheading">
              El servidor esta creando el reporte
            </Typography>
          </StepContent>
        </Step>
        <Step key={2}>
          <StepLabel>Validar Resultado</StepLabel>
          <StepContent>
            <Typography variant="subheading" gutterBottom={true}>
              Este es el resultado del reporte
            </Typography>
            <Button
              fullWidth={true}
              color="primary"
              variant="raised"
              onClick={() => window.open((report as ReportDocument).downloadUrl, '_blank')}
            >
              Descargar
            </Button>
          </StepContent>
        </Step>
      </Stepper>
    );
  }

  renderRasterCompareStepper(renders: Renders) {
    const { classes } = this.props;
    const { documentIds, report } = this.state;
    return (
      <Stepper
        activeStep={this.state.activeStep}
        orientation="vertical"
        className={classes.stepper}
      >
        <Step key={0}>
          <StepLabel>Seleccionar Rasters</StepLabel>
          <StepContent>
            <Typography variant="subheading" gutterBottom={true}>
              Elija de su biblioteca los rasters que desea utilizar en el reporte
            </Typography>
            <Button
              fullWidth={true}
              color="primary"
              variant="raised"
              disabled={!documentIds || !documentIds.length}
              onClick={() => {
                this.setState({ activeStep: 1 });
                renders.createReport();
              }}
            >
              Seleccionar
            </Button>
            {(!documentIds || !documentIds.length) && (
              <Typography color="error">
                Debe seleccionar un raster
              </Typography>
            )}
          </StepContent>
        </Step>
        <Step key={1}>
          <StepLabel>Crear Reporte</StepLabel>
          <StepContent>
            <Typography variant="subheading">
              El servidor esta creando el reporte
            </Typography>
          </StepContent>
        </Step>
        <Step key={2}>
          <StepLabel>Validar Resultado</StepLabel>
          <StepContent>
            <Typography variant="subheading" gutterBottom={true}>
              Este es el resultado del reporte
            </Typography>
            <Button
              fullWidth={true}
              color="primary"
              variant="raised"
              onClick={() => window.open((report as ReportDocument).downloadUrl, '_blank')}
            >
              Descargar
            </Button>
          </StepContent>
        </Step>
      </Stepper>
    );
  }

  renderRasterStepper(renders: Renders) {
    const { classes } = this.props;
    const { documentId, report } = this.state;
    return (
      <Stepper
        activeStep={this.state.activeStep}
        orientation="vertical"
        className={classes.stepper}
      >
        <Step key={0}>
          <StepLabel>Seleccionar Raster</StepLabel>
          <StepContent>
            <Typography variant="subheading" gutterBottom={true}>
              Elija de su biblioteca el raster que desea utilizar en el reporte
            </Typography>
            <Button
              fullWidth={true}
              color="primary"
              variant="raised"
              disabled={!documentId}
              onClick={() => {
                this.setState({ activeStep: 1 });
                renders.createReport();
              }}
            >
              Seleccionar
            </Button>
            {!documentId && (
              <Typography color="error">
                Debe seleccionar un raster
              </Typography>
            )}
          </StepContent>
        </Step>
        <Step key={1}>
          <StepLabel>Crear Reporte</StepLabel>
          <StepContent>
            <Typography variant="subheading">
              El servidor esta creando el reporte
            </Typography>
          </StepContent>
        </Step>
        <Step key={2}>
          <StepLabel>Validar Resultado</StepLabel>
          <StepContent>
            <Typography variant="subheading" gutterBottom={true}>
              Este es el resultado del reporte
            </Typography>
            <Button
              fullWidth={true}
              color="primary"
              variant="raised"
              onClick={() => window.open((report as ReportDocument).downloadUrl, '_blank')}
            >
              Descargar
            </Button>
          </StepContent>
        </Step>
      </Stepper>
    );
  }

  renderSingleRasterSelection = (renders: Renders) => {
    const {
      match: {
        params: { farmId }
      },
      classes
    } = this.props;
    const { documentId } = this.state;

    return (
      <FarmLibraryProvider
        farmId={farmId}
        first={9}
        filter={{ kind: ['raster-ndvi'] }}
      >
        {result =>
          <Paper square={true} elevation={1} className={classes.content}>
            <DocumentGrid
              selectable={true}
              documents={result.documents}
              loading={false}
              onFetchMore={result.fetchMore}
              selectedIDs={documentId ? [documentId] : []}
              onSelect={doc => {
                this.setState({ documentId: doc.id });
              }}
            />
          </Paper>
        }
      </FarmLibraryProvider>
    );
  }

  renderMultipleRasterSelection = (renders: Renders) => {
    const {
      match: {
        params: { farmId }
      },
      classes
    } = this.props;
    const { documentIds } = this.state;

    return (
      <FarmLibraryProvider
        farmId={farmId}
        first={9}
        filter={{ kind: ['raster-ndvi'] }}
      >
        {result =>
          <Paper square={true} elevation={1} className={classes.content}>
            <DocumentGrid
              selectable={true}
              documents={result.documents}
              loading={false}
              onFetchMore={result.fetchMore}
              selectedIDs={documentIds || []}
              onSelect={(doc: AnyDocument, checked: boolean) => {
                if (checked) {
                  this.setState({
                    documentIds: [...(documentIds || []), doc.id]
                  });
                } else {
                  this.setState({
                    documentIds: (documentIds || []).filter(d => d !== doc.id)
                  });
                }
              }}
            />
          </Paper>
        }
      </FarmLibraryProvider>
    );
  }

  renderSingleFieldSelection = (renders: Renders) => {
    const {
      match: {
        params: { farmId }
      },
      classes
    } = this.props;
    const { fieldId, sinceDate, untilDate } = this.state;

    return (
      <FarmFieldsProvider farmId={farmId}>
        {result =>
          <Paper square={true} elevation={1} className={classes.content}>
            <Paper square={true} elevation={0} className={classes.form}>
              <FormControl fullWidth={true} style={{marginBottom: 24}}>
                <InputLabel>Lote</InputLabel>
                <Select
                  value={fieldId || ''}
                  onChange={evt => this.setState({ fieldId: evt.target.value })}
                  fullWidth={true}
                >
                  {result.fields.map(field => (
                    <MenuItem key={field.id} value={field.id}>
                      {field.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <DatePicker
                fullWidth={true}
                label="Desde"
                clearable={true}
                disableFuture={true}
                value={sinceDate || null}
                onChange={d => this.setState({ sinceDate: d })}
                maxDate={untilDate}
                clearLabel="Limpiar"
                labelFunc={date =>
                  date ? date.format('DD/MM/YYYY') : ''
                }
                style={{marginBottom: 24}}
              />
              <DatePicker
                fullWidth={true}
                label="Hasta"
                clearable={true}
                disableFuture={true}
                value={untilDate || null}
                onChange={d => this.setState({ untilDate: d })}
                minDate={sinceDate}
                clearLabel="Limpiar"
                labelFunc={date =>
                  date ? date.format('DD/MM/YYYY') : ''
                }
              />
            </Paper>
          </Paper>
        }
      </FarmFieldsProvider>
    );
  }

  renderMultipleFieldSelection = (renders: Renders) => {
    const {
      match: {
        params: { farmId }
      },
      classes
    } = this.props;
    const { fieldIds } = this.state;

    return (
      <FarmFieldsProvider farmId={farmId}>
        {result =>
          <Paper square={true} elevation={1} className={classes.content}>
            <Paper square={true} elevation={0} className={classes.form}>
              <FormControl fullWidth={true}>
                <InputLabel>Lotes</InputLabel>
                <Select
                  multiple={true}
                  value={fieldIds || []}
                  onChange={evt =>
                    this.setState({ fieldIds: evt.target.value as any })
                  }
                  fullWidth={true}
                  renderValue={selected => (
                    <div className={classes.chips}>
                      {(selected as string[]).map(value => (
                        <Chip
                          key={value}
                          label={(result.fields.find(f => f.id === value) as any).name}
                          className={classes.chip}
                        />
                      ))}
                    </div>
                  )}
                >
                  {result.fields.map(field => (
                    <MenuItem key={field.id} value={field.id}>
                      <Checkbox
                        checked={(fieldIds || []).indexOf(field.id) > -1}
                      />
                      <ListItemText primary={field.name} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Paper>
          </Paper>
        }
      </FarmFieldsProvider>
    );
  }

  renderZoneAnalysisSelection = (renders: Renders) => {
    const {
      match: {
        params: { farmId }
      },
      classes
    } = this.props;
    const { zoneAnalysisDocumentId } = this.state;

    return (
      <FarmLibraryProvider
        farmId={farmId}
        first={9}
        filter={{ kind: ['zone-analysis'] }}
      >
        {result =>
          <Paper square={true} elevation={1} className={classes.content}>
            <DocumentGrid
              selectable={true}
              documents={result.documents}
              loading={false}
              onFetchMore={result.fetchMore}
              selectedIDs={zoneAnalysisDocumentId ? [zoneAnalysisDocumentId] : []}
              onSelect={(doc: AnyDocument, checked: boolean) => {
                this.setState({ zoneAnalysisDocumentId: doc.id });
              }}
            />
          </Paper>
        }
      </FarmLibraryProvider>
    );
  }

  renderReportCreating = (renders: Renders) => {
    const { classes } = this.props;
    return (
      <Paper square={true} elevation={1} className={classes.contentCentered}>
        <CircularProgress size={150} />
        <Typography variant="title" color="textSecondary">
          Creando reporte
        </Typography>
      </Paper>
    );
  }

  renderReportCreated = (renders: Renders) => {
    const { classes } = this.props;
    return (
      <Paper square={true} elevation={1} className={classes.contentCentered}>
        <Typography variant="title" color="textSecondary">
          Reporte creado
        </Typography>
      </Paper>
    );
  }

  renderStep = (renders: Renders) => {
    const {
      match: {
        params: { type }
      }
    } = this.props;
    const { activeStep } = this.state;

    switch (type) {
      case 'raster':
      case 'raster-zone':
        return [
          this.renderSingleRasterSelection,
          this.renderReportCreating,
          this.renderReportCreated
        ][activeStep](renders);
      case 'raster-compare':
        return [
          this.renderMultipleRasterSelection,
          this.renderReportCreating,
          this.renderReportCreated
        ][activeStep](renders);
      case 'tracking-field':
        return [
          this.renderSingleFieldSelection,
          this.renderZoneAnalysisSelection,
          this.renderReportCreating,
          this.renderReportCreated
        ][activeStep](renders);
      case 'tracking-multiple-fields':
        return [
          this.renderMultipleFieldSelection,
          this.renderReportCreating,
          this.renderReportCreated
        ][activeStep](renders);
      default:
        return null;
    }
  }

  renderStepper(renders: Renders) {
    const {
      match: {
        params: { type }
      }
    } = this.props;
    switch (type) {
      case 'tracking-field':
        return this.renderFieldStepper(renders);
      case 'tracking-multiple-fields':
        return this.renderFieldsStepper(renders);
      case 'raster':
      case 'raster-zone':
        return this.renderRasterStepper(renders);
      case 'raster-compare':
        return this.renderRasterCompareStepper(renders);
      default:
        return null;
    }
  }

  composeProviders(children: (r: Renders) => React.ReactNode) {
    const {
      match: {
        params: { farmId, type }
      }
    } = this.props;
    const {
      fieldId,
      fieldIds,
      documentId,
      documentIds,
      sinceDate,
      untilDate,
      zoneAnalysisDocumentId
    } = this.state;

    return (
      <LayoutToolsProvider>
        {layout => (
          <FarmProvider farmId={farmId}>
            {(farm, loading) =>
              <CreateReportProvider
                farmId={farmId}
                type={type}
                params={{
                  fieldId,
                  fieldIds,
                  documentId,
                  documentIds,
                  sinceDate,
                  untilDate,
                  zoneAnalysisDocumentId
                }}
                onCompleted={report => this.onReportCreated(report)}
              >
                {createReport =>
                  children({
                    layout,
                    loading: loading,
                    farm: farm,
                    createReport: createReport.create
                  })
                }
              </CreateReportProvider>
            }
          </FarmProvider>
        )}
      </LayoutToolsProvider>
    );
  }

  render() {
    const { classes } = this.props;

    return this.composeProviders(renders => (
      <main className={classes.root}>
        <SidePanel>
          <div style={{display: 'flex'}}>
            <div style={{flex: 0, marginRight: 16, alignItems: 'center'}}>
              <IconButton onClick={() => this.props.history.goBack()}>
                <BackIcon />
              </IconButton>
            </div>
            <div style={{flex: 1}}>
              <Typography variant="subheading">Crear Reporte</Typography>
              <Typography variant="headline">
                {this.getReportType()}
              </Typography>
            </div>
          </div>
          {this.renderStepper(renders)}
        </SidePanel>

        {this.renderStep(renders)}
      </main>
    ));
  }
}

export default withStyles(styles)(withRouter(ReportsCreation));



// WEBPACK FOOTER //
// ./src/containers/ReportsCreation.tsx